import { useEffect, useRef } from "react";
import { loadAffirm } from "../lib/affirm";

// Renders Affirm's official "as low as $XX/mo" promotional messaging widget.
// Affirm.js populates the element (with the real monthly figure + promo terms) on refresh.
// We keep a single stable element and update its amount in place (rather than remounting),
// then refresh reliably so the message never blanks out when the price/condition changes.
export default function AffirmPromo({ amount, pageType = "product", className = "", testId = "affirm-promo" }) {
  const ref = useRef(null);
  const cents = Math.round(Number(amount || 0) * 100);
  const show = cents >= 5000;

  useEffect(() => {
    if (!show || !ref.current) return undefined;
    let cancelled = false;
    const timers = [];
    // Keep the element's amount current before refreshing.
    ref.current.setAttribute("data-amount", String(cents));
    loadAffirm()
      .then((affirm) => {
        if (cancelled || !affirm?.ui?.refresh) return;
        const doRefresh = () => { try { affirm.ui.refresh(); } catch { /* no-op */ } };
        // Refresh now, on Affirm's ready hook, and once more shortly after paint —
        // this covers the case where the element is empty right after an update.
        if (affirm.ui.ready) affirm.ui.ready(doRefresh);
        doRefresh();
        timers.push(setTimeout(doRefresh, 300));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [cents, show]);

  if (!show) return null;

  return (
    <p
      ref={ref}
      className={`affirm-as-low-as text-sm leading-relaxed text-muted-foreground ${className}`}
      data-page-type={pageType}
      data-amount={cents}
      data-affirm-color="blue"
      data-testid={testId}
    />
  );
}
